import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { conditions } from '@character-creator/data';
import type { Condition, ConditionName } from '@character-creator/types';
import { LinkedText } from '../components/LinkedText';

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function ConditionsReferencePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedConditionId, setExpandedConditionId] = useState<string | null>(null);

  const filteredConditions = useMemo(() => {
    if (!searchQuery) return conditions;

    const query = searchQuery.toLowerCase();
    return conditions.filter(
      (condition) =>
        condition.name.toLowerCase().includes(query) ||
        condition.description.toLowerCase().includes(query) ||
        condition.effects.some((e) => e.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  const toggleConditionExpanded = (conditionId: string) => {
    setExpandedConditionId((prev) => (prev === conditionId ? null : conditionId));
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-card/95 backdrop-blur-sm border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/create"
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            >
              <BackIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-text-primary">Conditions Reference</h1>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="sticky top-[65px] z-10 bg-background-primary border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search conditions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-background-card border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-gold"
            />
          </div>
        </div>
      </div>

      {/* Intro Text */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <p className="text-sm text-text-secondary">
          Conditions alter a creature's capabilities in various ways and can arise as a result of
          spells, class features, monster attacks, or other effects. Most conditions are
          impairments, but a few can be advantageous.
        </p>
        <p className="text-sm text-text-muted mt-2">
          Showing {filteredConditions.length} of {conditions.length} conditions
        </p>
      </div>

      {/* Condition List */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        {filteredConditions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-card flex items-center justify-center border border-border-default">
              <ConditionIcon className="w-8 h-8 text-text-muted" />
            </div>
            <p className="text-text-secondary">No conditions match your search.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 px-4 py-2 text-accent-gold hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredConditions.map((condition) => (
              <ConditionCard
                key={condition.id}
                condition={condition}
                isExpanded={expandedConditionId === condition.id}
                onToggle={() => toggleConditionExpanded(condition.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

interface ConditionCardProps {
  condition: Condition;
  isExpanded: boolean;
  onToggle: () => void;
}

function ConditionCard({ condition, isExpanded, onToggle }: ConditionCardProps) {
  const hasLevels = condition.id === 'exhaustion' && condition.levels;

  return (
    <div
      id={condition.id}
      className="bg-background-card border border-border-default rounded-lg overflow-hidden"
    >
      {/* Collapsed View */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-background-elevated transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-text-primary">{condition.name}</h3>
              {hasLevels && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-red/20 text-accent-red rounded">
                  6 Levels
                </span>
              )}
              {condition.includesConditions && condition.includesConditions.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded">
                  Includes other conditions
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-text-muted line-clamp-2">
              <LinkedText excludePages={['conditions']}>{condition.description}</LinkedText>
            </p>
          </div>
          <div className="shrink-0">
            <ConditionTypeIcon conditionId={condition.id} />
          </div>
        </div>
        <div className="flex items-center justify-center mt-2">
          <ChevronIcon
            className={`w-5 h-5 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expanded View */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-default">
          <div className="pt-4 space-y-4">
            {/* Effects */}
            <div>
              <h4 className="text-sm font-semibold text-text-primary mb-2">Effects</h4>
              <ul className="space-y-1.5">
                {condition.effects.map((effect, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-accent-gold mt-1">*</span>
                    <LinkedText excludePages={['conditions']}>{effect}</LinkedText>
                  </li>
                ))}
              </ul>
            </div>

            {/* Exhaustion Levels */}
            {hasLevels && condition.levels && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">Exhaustion Levels</h4>
                <div className="bg-background-elevated rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-default">
                        <th className="px-3 py-2 text-left font-medium text-text-muted">Level</th>
                        <th className="px-3 py-2 text-left font-medium text-text-muted">Effect</th>
                      </tr>
                    </thead>
                    <tbody>
                      {condition.levels.map((level) => (
                        <tr
                          key={level.level}
                          className={`border-b border-border-default last:border-0 ${
                            level.level === 6 ? 'bg-accent-red/10' : ''
                          }`}
                        >
                          <td className="px-3 py-2 font-medium text-text-primary">
                            {level.level}
                          </td>
                          <td
                            className={`px-3 py-2 ${level.level === 6 ? 'text-accent-red font-semibold' : 'text-text-secondary'}`}
                          >
                            {level.effect}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Includes Conditions */}
            {condition.includesConditions && condition.includesConditions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">
                  Also Includes These Conditions
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {condition.includesConditions.map((includedId) => (
                    <a
                      key={includedId}
                      href={`#${includedId}`}
                      className="px-2 py-1 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded hover:bg-accent-purple/30 transition-colors"
                    >
                      {capitalizeFirst(includedId)}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Common Sources */}
            {condition.commonSources && condition.commonSources.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">Common Sources</h4>
                <div className="flex flex-wrap gap-1.5">
                  {condition.commonSources.map((source, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium bg-background-elevated text-text-secondary rounded"
                    >
                      <LinkedText excludePages={['conditions']}>{source}</LinkedText>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConditionTypeIcon({ conditionId }: { conditionId: ConditionName }) {
  // Return different colored circles based on condition severity/type
  const getColor = () => {
    switch (conditionId) {
      case 'unconscious':
      case 'paralyzed':
      case 'petrified':
      case 'stunned':
        return 'bg-accent-red'; // Severe
      case 'exhaustion':
      case 'frightened':
      case 'poisoned':
        return 'bg-accent-orange'; // Moderate
      case 'blinded':
      case 'deafened':
      case 'charmed':
        return 'bg-accent-gold'; // Sensory/Mental
      case 'grappled':
      case 'restrained':
      case 'prone':
        return 'bg-accent-blue'; // Movement
      case 'invisible':
        return 'bg-accent-purple'; // Beneficial
      case 'incapacitated':
      default:
        return 'bg-text-muted';
    }
  };

  return <div className={`w-3 h-3 rounded-full ${getColor()}`} />;
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function ConditionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}
